import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, Dice5, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const WheelOfFortune = ({ userId, onSpinComplete }) => {
    const [canSpin, setCanSpin] = useState(false);
    const [isSpinning, setIsSpinning] = useState(false);
    const [result, setResult] = useState(null);
    const [prizes, setPrizes] = useState([]);
    const [rotation, setRotation] = useState(0);

    const checkStatus = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/wheel/status/${userId}`);
            setCanSpin(response.data.can_spin);
            setPrizes(response.data.prizes || []);
        } catch (error) {
            console.error('Error checking wheel status:', error);
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            checkStatus();
        }
    }, [userId, checkStatus]);

    const handleSpin = async () => {
        if (!canSpin || isSpinning) return;

        setIsSpinning(true);
        // Visual rotation: multiple full turns + random offset
        const newRotation = rotation + 1800 + Math.floor(Math.random() * 360);
        setRotation(newRotation);

        try {
            // Small delay to let the animation start
            await new Promise(resolve => setTimeout(resolve, 3000));

            const response = await axios.post(`${API}/wheel/spin/${userId}`);
            const prize = response.data.prize;

            setResult(prize);
            setCanSpin(false);

            toast({
                title: `RIULTATO: ${prize.result} 🤌`,
                description: `${prize.name}: ${prize.icon}`,
            });

            if (onSpinComplete) onSpinComplete(response.data);
        } catch (error) {
            console.error('Error spinning wheel:', error);
            toast({
                title: "Errore dal Casinò del Don 🎰",
                description: error.response?.data?.detail || "Qualcosa è andato storto...",
                variant: "destructive"
            });
        } finally {
            setIsSpinning(false);
        }
    };

    return (
        <Card className="bg-slate-900 border-amber-500/30 overflow-hidden">
            <CardHeader className="p-4 border-b border-amber-500/20 bg-gradient-to-r from-amber-900/20 to-transparent">
                <CardTitle className="text-amber-400 flex items-center gap-2 text-lg">
                    <Dice5 className="w-5 h-5" />
                    La Ruota della Fortuna
                </CardTitle>
            </CardHeader>
            <CardContent className="p-6 flex flex-col items-center gap-6">
                {/* The Wheel Visual */}
                <div className="relative w-48 h-48 sm:w-64 sm:h-64">
                    {/* External ring */}
                    <div className="absolute inset-0 rounded-full border-8 border-amber-600/50 shadow-[0_0_20px_rgba(217,119,6,0.2)]"></div>

                    {/* Spinning core */}
                    <div
                        className="w-full h-full rounded-full border-4 border-slate-800 transition-transform duration-[3000ms] ease-out overflow-hidden flex items-center justify-center bg-slate-800 shadow-inner"
                        style={{ transform: `rotate(${rotation}deg)` }}
                    >
                        {/* Wheel segments - CSS Gradient for 8 segments */}
                        <div className="absolute inset-0 opacity-40 bg-[conic-gradient(#b45309_0deg_45deg,#1e293b_45deg_90deg,#92400e_90deg_135deg,#1e293b_135deg_180deg,#78350f_180deg_225deg,#1e293b_225deg_270deg,#451a03_270deg_315deg,#1e293b_315deg_360deg)]"></div>

                        {/* Center decoration */}
                        <div className="absolute inset-[30%] rounded-full bg-slate-900 border-2 border-amber-500/50 flex items-center justify-center z-10 shadow-lg">
                            <span className="text-4xl sm:text-6xl animate-pulse">🤌</span>
                        </div>

                        {/* Icons on the wheel */}
                        {prizes.length > 0 && prizes.map((p, i) => (
                            <div
                                key={p.id}
                                className="absolute h-1/2 w-8 origin-bottom flex flex-col items-center pt-2"
                                style={{ transform: `rotate(${i * 45 + 22.5}deg)`, top: '0%' }}
                            >
                                <span className="text-xl sm:text-2xl" style={{ transform: 'rotate(180deg)' }}>{p.icon}</span>
                            </div>
                        ))}
                    </div>

                    {/* Fixed Arrow (Indicator) */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[25px] border-t-amber-500 z-20 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"></div>
                </div>

                {/* Status & Control */}
                <div className="text-center w-full">
                    {canSpin ? (
                        <div className="space-y-3">
                            <p className="text-slate-400 text-sm">
                                Hai un giro gratis oggi! Tenta la fortuna con il Don.
                            </p>
                            <Button
                                onClick={handleSpin}
                                disabled={isSpinning}
                                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 font-bold text-lg h-12 shadow-lg shadow-amber-900/20"
                            >
                                {isSpinning ? "GIRANDO... 🎰" : "GIRA LA RUOTA! 🤌"}
                            </Button>
                        </div>
                    ) : result ? (
                        <div className="space-y-4 animate-in fade-in zoom-in">
                            <div className="p-4 rounded-lg bg-amber-900/20 border border-amber-500/30">
                                <p className="text-amber-300 font-bold text-xl uppercase tracking-wider mb-1">
                                    🎉 {result.result} 🎉
                                </p>
                                <p className="text-slate-300 text-sm">{result.name}</p>
                            </div>
                            <p className="text-slate-500 text-xs italic">Hai già tentato la fortuna oggi. Torna domani!</p>
                        </div>
                    ) : (
                        <div className="p-4 rounded-lg bg-slate-800/40 border border-slate-700/50">
                            <p className="text-slate-400 text-sm">Tenta la fortuna ogni giorno per fantastici premi (o minacce).</p>
                            <p className="text-slate-500 text-xs mt-3">Hai già girato oggi, compare. A domani!</p>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default WheelOfFortune;
